/**
 * Calculate weighted average electricity price
 * based on consumption and Nord Pool prices
 */

export function calculateWeightedPrice(consumptionData, priceData = null) {
  let totalPurchase = 0
  let totalSale = 0
  let totalPurchaseCost = 0
  let totalSaleRevenue = 0

  // For now, use a sample price if priceData not provided
  // TODO: Fetch actual prices from Worker API or local DB
  const samplePrice = 0.10 // EUR/kWh

  consumptionData.forEach(record => {
    const kwh = record.kwh
    
    // ESO format: "2026-02-01T00:15:00+02:00"
    // DB format: "2026-02-01 00:15:00"
    const dt = record.datetime
    const lookupKey = dt.substring(0, 10) + ' ' + dt.substring(11, 19)
    
    const price = priceData?.[lookupKey] || samplePrice

    if (record.type === 'P+') {
      totalPurchase += kwh
      totalPurchaseCost += kwh * price
    } else if (record.type === 'P-') {
      totalSale += kwh
      totalSaleRevenue += kwh * price
    }
  })

  // Calculate weighted average price
  const weightedPrice = totalPurchase > 0 
    ? totalPurchaseCost / totalPurchase 
    : 0

  // Calculate balance (revenue - cost)
  const balance = totalSaleRevenue - totalPurchaseCost

  return {
    totalPurchase,
    totalSale,
    totalPurchaseCost,
    totalSaleRevenue,
    weightedPrice,
    balance,
    netConsumption: totalPurchase - totalSale,
    netCost: totalPurchaseCost - totalSaleRevenue
  }
}

/**
 * Calculate statistics by period (daily, weekly, monthly)
 */
export function calculatePeriodStatistics(consumptionData, period = 'daily') {
  const grouped = {}

  consumptionData.forEach(record => {
    const date = new Date(record.datetime)
    let key

    switch (period) {
      case 'daily':
        key = date.toISOString().split('T')[0]
        break
      case 'weekly':
        const weekStart = new Date(date)
        weekStart.setDate(date.getDate() - date.getDay())
        key = weekStart.toISOString().split('T')[0]
        break
      case 'monthly':
        key = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`
        break
      default:
        key = date.toISOString().split('T')[0]
    }

    if (!grouped[key]) {
      grouped[key] = []
    }

    grouped[key].push(record)
  })

  // Calculate statistics for each period
  const statistics = {}
  Object.keys(grouped).forEach(key => {
    statistics[key] = calculateWeightedPrice(grouped[key])
  })

  return statistics
}

/**
 * Find peak consumption hours
 */
export function findPeakHours(consumptionData, topN = 5) {
  const hourlyConsumption = {}

  consumptionData.forEach(record => {
    if (record.type !== 'P+') return // Only count purchases

    const date = new Date(record.datetime)
    const hour = date.getHours()

    if (!hourlyConsumption[hour]) {
      hourlyConsumption[hour] = 0
    }

    hourlyConsumption[hour] += record.kwh
  })

  // Sort by consumption
  const sorted = Object.entries(hourlyConsumption)
    .map(([hour, kwh]) => ({ hour: parseInt(hour), kwh }))
    .sort((a, b) => b.kwh - a.kwh)

  return sorted.slice(0, topN)
}
